import React, { useEffect, useState } from "react";
import { getCdpSessionDetail } from "../../api";
import { fmtLocation } from "../../utils/format";

/* ───────────── helpers ───────────── */
function fmtTime(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", second: "2-digit", hour12: true });
}

function relativeGap(prev, curr) {
  if (!prev || !curr) return null;
  const diffSec = Math.round((new Date(curr) - new Date(prev)) / 1000);
  if (diffSec < 5) return null;
  if (diffSec < 60) return `${diffSec}s later`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ${diffSec % 60}s later`;
  return `${Math.floor(diffSec / 3600)}h ${Math.floor((diffSec % 3600) / 60)}m later`;
}

function stripDomain(url) {
  if (!url) return "";
  try { return new URL(url).pathname; } catch { return url; }
}

function truncate(str, max = 50) {
  if (!str || str.length <= max) return str || "";
  return str.slice(0, max) + "\u2026";
}

/* Timeline event config */
const EVENT_CONFIG = {
  page_view:          { label: "Viewed", dot: "bg-blue-500", bold: false },
  book_now_click:     { label: "Clicked Book Now", dot: "bg-emerald-500", bold: true },
  booking_completed:  { label: "Booking Completed", dot: "bg-amber-500", bold: true, highlight: true },
  email_capture:      { label: "Email captured", dot: "bg-purple-500", bold: false },
  phone_click:        { label: "Clicked phone number", dot: "bg-blue-500", bold: false },
  video_play:         { label: "Played video", dot: "bg-teal-500", bold: false },
  video_milestone:    { label: "Video milestone", dot: "bg-gray-400", bold: false, small: true },
  form_submit:        { label: "Submitted form", dot: "bg-blue-500", bold: false },
  outbound_click:     { label: "Clicked external link", dot: "bg-gray-400", bold: false },
  exit_intent:        { label: "Exit intent detected", dot: "bg-red-400", bold: false, small: true },
  booking_hover:      { label: "Hovered booking widget", dot: "bg-yellow-400", bold: false, small: true },
  gallery_navigate:   { label: "Browsed photo gallery", dot: "bg-gray-400", bold: false, small: true },
};
const SKIP_EVENTS = new Set(["perf_metrics", "visibility_change"]);

const CHANNEL_COLORS = {
  "Paid Search": "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  "Paid Social": "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300",
  "Organic Search": "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  "Organic Social": "bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300",
  "Direct": "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
  "Maps": "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  "Email": "bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300",
  "AI": "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300",
  "Referral": "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
};

/* ═══════════════════ COMPONENT ═══════════════════ */
export default function CdpSessionDetail({ clientId, sessionId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clientId || !sessionId) return;
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const resp = await getCdpSessionDetail(clientId, sessionId);
        if (!cancelled) setData(resp?.data || null);
      } catch (e) {
        console.error("Session detail error:", e);
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [clientId, sessionId]);

  const h = data?.header || {};
  const timeline = data?.timeline || [];

  // Group consecutive heartbeats
  const processedTimeline = [];
  let heartbeatGroup = null;

  for (const evt of timeline) {
    if (SKIP_EVENTS.has(evt.event_type)) continue;

    if (evt.event_type === "heartbeat") {
      if (!heartbeatGroup) heartbeatGroup = { count: 0, start: evt.event_timestamp };
      heartbeatGroup.count++;
      heartbeatGroup.end = evt.event_timestamp;
      continue;
    }

    // Flush heartbeat group
    if (heartbeatGroup) {
      processedTimeline.push({ type: "heartbeat_group", count: heartbeatGroup.count, timestamp: heartbeatGroup.start });
      heartbeatGroup = null;
    }

    processedTimeline.push({ ...evt, type: "event" });
  }
  if (heartbeatGroup) {
    processedTimeline.push({ type: "heartbeat_group", count: heartbeatGroup.count, timestamp: heartbeatGroup.start });
  }

  const hasUtms = h.utm_source || h.utm_medium || h.utm_campaign;
  const hasClickIds = h.fbclid || h.gclid || h.msclkid;
  const location = fmtLocation(h.city, h.region, h.country);

  return (
    <div className="fixed inset-y-0 right-0 z-50 flex">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/20 dark:bg-black/40" onClick={onClose} />

      {/* Panel */}
      <div className="relative ml-auto w-[480px] bg-white dark:bg-gray-900 shadow-xl overflow-y-auto border-l border-gray-200 dark:border-gray-800">
        {loading ? (
          <div className="flex items-center justify-center h-full text-gray-400">Loading session...</div>
        ) : !data ? (
          <div className="flex items-center justify-center h-full text-gray-400">Session not found</div>
        ) : (
          <>
            {/* ─── Header ─── */}
            <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-5 py-4 z-10">
              <div className="flex items-center justify-between mb-3">
                <button type="button" onClick={onClose} className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 flex items-center gap-1">
                  &larr; Back
                </button>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400 text-sm font-bold shrink-0">
                  {h.person_name ? h.person_name[0].toUpperCase() : "\ud83d\udc64"}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">
                    {h.person_name || (h.person_email ? h.person_email : `Anonymous (${h.cv_id?.slice(0, 8)}...)`)}
                  </div>
                  {h.person_email && <div className="text-xs text-gray-500 dark:text-gray-400">{h.person_email}</div>}
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {location || "Unknown location"} &middot; {h.device || "Unknown"}
                  </div>
                </div>
              </div>

              {/* Meta */}
              <div className="mt-3 space-y-1.5 text-xs text-gray-600 dark:text-gray-400">
                <div className="flex items-center gap-2">
                  <span className="text-gray-400 dark:text-gray-500 w-16 shrink-0">Channel</span>
                  {h.channel ? (
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${CHANNEL_COLORS[h.channel] || CHANNEL_COLORS["Direct"]}`}>
                      {h.channel}
                    </span>
                  ) : <span>—</span>}
                </div>
                {h.referrer && (
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400 dark:text-gray-500 w-16 shrink-0">Referrer</span>
                    <span className="truncate">{h.referrer}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <span className="text-gray-400 dark:text-gray-500 w-16 shrink-0">Device</span>
                  <span>{[h.device, h.browser, h.os].filter(Boolean).join(" \u00b7 ") || "—"}</span>
                </div>
                {h.language && (
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400 dark:text-gray-500 w-16 shrink-0">Language</span>
                    <span>{h.language}{h.timezone ? ` \u00b7 ${h.timezone}` : ""}</span>
                  </div>
                )}
                {h.connection && (
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400 dark:text-gray-500 w-16 shrink-0">Network</span>
                    <span>{h.connection}</span>
                  </div>
                )}
                {h.ad_blocker && (
                  <div className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                    <span>{"\u26a0\ufe0f"}</span> Ad Blocker Detected
                  </div>
                )}
                {hasUtms && (
                  <div className="flex items-start gap-2">
                    <span className="text-gray-400 dark:text-gray-500 w-16 shrink-0">UTMs</span>
                    <span className="truncate">
                      {[h.utm_source && `source=${h.utm_source}`, h.utm_medium && `medium=${h.utm_medium}`, h.utm_campaign && `camp=${h.utm_campaign}`].filter(Boolean).join(" ")}
                    </span>
                  </div>
                )}
                {hasClickIds && (
                  <div className="flex items-start gap-2">
                    <span className="text-gray-400 dark:text-gray-500 w-16 shrink-0">Click IDs</span>
                    <span className="truncate text-[10px] font-mono">
                      {[h.fbclid && `fbclid: ${h.fbclid.slice(0, 12)}...`, h.gclid && `gclid: ${h.gclid.slice(0, 12)}...`, h.msclkid && `msclkid: ${h.msclkid.slice(0, 12)}...`].filter(Boolean).join(" ")}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* ─── Timeline ─── */}
            <div className="px-5 py-4">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">Timeline</h4>
              <div className="relative pl-5 border-l-2 border-gray-200 dark:border-gray-700 space-y-0">
                {processedTimeline.map((item, i) => {
                  const prev = i > 0 ? processedTimeline[i - 1] : null;
                  const gap = relativeGap(prev?.timestamp || prev?.event_timestamp, item.timestamp || item.event_timestamp);

                  // Heartbeat group
                  if (item.type === "heartbeat_group") {
                    return (
                      <div key={`hb-${i}`} className="relative py-1.5">
                        <div className="absolute -left-[9px] top-2.5 w-2.5 h-2.5 rounded-full bg-gray-300 dark:bg-gray-600 border-2 border-white dark:border-gray-900"></div>
                        <div className="text-[11px] text-gray-400 dark:text-gray-500 italic ml-2">
                          Active for ~{item.count * 30}s
                        </div>
                      </div>
                    );
                  }

                  const cfg = EVENT_CONFIG[item.event_type] || { label: item.event_type, dot: "bg-gray-400", bold: false };
                  if (cfg.small && i > 0) {
                    // Render small/subtle events inline
                  }

                  const pageInfo = item.event_type === "page_view"
                    ? `: ${truncate(item.page_title || stripDomain(item.page_url), 45)}`
                    : item.page_url ? ` \u2192 ${truncate(stripDomain(item.page_url), 35)}` : "";

                  return (
                    <React.Fragment key={`evt-${i}`}>
                      {gap && (
                        <div className="py-1 ml-2">
                          <span className="text-[10px] text-gray-300 dark:text-gray-600">\u2014 {gap} \u2014</span>
                        </div>
                      )}
                      <div className={`relative py-2 ${cfg.highlight ? "bg-amber-50 dark:bg-amber-900/10 -mx-2 px-2 rounded" : ""}`}>
                        <div className={`absolute -left-[9px] top-3 w-2.5 h-2.5 rounded-full ${cfg.dot} border-2 border-white dark:border-gray-900`}></div>
                        <div className="flex items-start justify-between ml-2">
                          <div className={`text-xs ${cfg.bold ? "font-semibold text-gray-900 dark:text-white" : "text-gray-700 dark:text-gray-300"} ${cfg.small ? "text-[11px] text-gray-400 dark:text-gray-500" : ""}`}>
                            {cfg.label}{pageInfo}
                          </div>
                          <span className="text-[10px] text-gray-400 dark:text-gray-500 shrink-0 ml-2">{fmtTime(item.event_timestamp)}</span>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>

              {processedTimeline.length === 0 && (
                <div className="text-center py-6 text-gray-400 text-sm">No events in this session</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
